import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search, SlidersHorizontal, Star, Upload, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatINRCompact } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { ASSET_SCOPES, ARCHIVED_STATUSES, saleTypeSection } from "@/lib/assetScopes";

const STATUS_FILTERS = [
  "All",
  "Pending Review",
  "Active",
  "Under Matching",
  "Bid Submitted",
  "Won",
  "Lost",
  "Closed",
  "Expired",
];

function useLabelMap(listType) {
  const { data = [] } = useQuery({
    queryKey: ["master", listType, "all-with-inactive"],
    queryFn: async () => {
      const { data } = await api.get(`/master-data/${listType}`, {
        params: { include_inactive: true },
      });
      return data;
    },
    staleTime: 60_000,
  });
  return useMemo(() => Object.fromEntries(data.map((d) => [d.id, d.label])), [data]);
}

export default function AssetsList({ scope = "auction" }) {
  const cfg = ASSET_SCOPES[scope] || ASSET_SCOPES.auction;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState("All");
  const [search, setSearch] = useState("");

  const { data: allAssets = [], isLoading } = useQuery({
    queryKey: ["assets", status, search],
    queryFn: async () => {
      const params = {};
      if (status !== "All") params.status = status;
      if (search) params.q = search;
      const { data } = await api.get("/assets", { params });
      return data;
    },
    keepPreviousData: true,
  });

  const categoryMap = useLabelMap("asset_category");
  const typeMap = useLabelMap("asset_type");
  const cityMap = useLabelMap("city");
  const saleTypeMap = useLabelMap("sale_type");
  const { data: saleTypeOptions = [] } = useQuery({
    queryKey: ["master", "sale_type", "all-with-inactive"],
    queryFn: async () => (await api.get("/master-data/sale_type", { params: { include_inactive: true } })).data,
    staleTime: 60_000,
  });

  // Display-only scope filter: keep assets whose Sale Type belongs to this section.
  // Use asset_section (dynamic) so it stays consistent with the Add-Asset dropdown and
  // Master Data grouping — a resale-tagged Sale Type (incl. custom ones) always lands here.
  const targetSection = cfg.key === "land" ? "resale" : cfg.key === "auction" ? "auction" : null;
  const allowedSaleTypeIds = useMemo(
    () =>
      new Set(
        saleTypeOptions
          .filter((o) => saleTypeSection(o) === targetSection)
          .map((o) => o.id)
      ),
    [saleTypeOptions, targetSection]
  );
  const assets = useMemo(() => {
    if (cfg.statusMode === "archived") {
      return allAssets.filter((a) => ARCHIVED_STATUSES.includes(a.status));
    }
    return allAssets.filter((a) => a.status === "Active" && allowedSaleTypeIds.has(a.sale_type_id));
  }, [allAssets, allowedSaleTypeIds, cfg]);

  const addAssetPath = `/assets/new?scope=${cfg.key}`;

  const assetIds = useMemo(() => assets.map((a) => a.id), [assets]);
  const { data: rels = {} } = useQuery({
    queryKey: ["asset-relationships", assetIds],
    queryFn: async () => {
      const { data } = await api.post("/assets/relationship-summary", { asset_ids: assetIds });
      return data;
    },
    enabled: assetIds.length > 0,
    keepPreviousData: true,
  });

  return (
    <div className="p-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">{cfg.eyebrow}</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">{cfg.title}</h1>
          <p className="text-sm text-slate-600 mt-1.5">
            {cfg.statusMode === "archived"
              ? "Completed, closed, and parked assets (Won / Lost / Closed / Expired / On Hold due to Litigation / Inactive). Still fully editable."
              : user?.role === "admin"
              ? "All distressed properties across the firm."
              : "Assets you've sourced."}
          </p>
        </div>
        <Button
          className="bg-[#1C1F26] hover:bg-[#2E3138] text-white"
          onClick={() => navigate(addAssetPath)}
          data-testid="add-asset-button"
          style={{ display: cfg.noAdd ? "none" : undefined }}
        >
          <Plus className="h-4 w-4 mr-1.5" /> Add Asset
        </Button>
      </div>

      {/* Filter bar */}
      <div className="mt-6 bg-white border border-slate-200 rounded-md p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name, asset code, address…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="assets-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-slate-500" />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[180px]" data-testid="assets-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((s) => (
                <SelectItem key={s} value={s} data-testid={`status-filter-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-slate-500 font-mono-num ml-auto">
          {assets.length} {assets.length === 1 ? "asset" : "assets"}
        </div>
      </div>

      {/* Table */}
      <div className="mt-4 bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table w-full text-left">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="label-eyebrow !text-slate-600">Asset</th>
                <th className="label-eyebrow !text-slate-600">Category</th>
                <th className="label-eyebrow !text-slate-600">Location</th>
                <th className="label-eyebrow !text-slate-600 text-right">Offer Price</th>
                <th className="label-eyebrow !text-slate-600">Sale Type</th>
                <th className="label-eyebrow !text-slate-600">Offered To</th>
                <th className="label-eyebrow !text-slate-600">Shared with Brokers</th>
                <th className="label-eyebrow !text-slate-600">Matched Leads</th>
                <th className="label-eyebrow !text-slate-600">Matched Brokers</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td colSpan={9} className="!py-3">
                      <Skeleton className="h-8 w-full" />
                    </td>
                  </tr>
                ))
              ) : assets.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-16 text-center">
                    {(status !== "All" || search) ? (
                      <div className="text-sm text-slate-500" data-testid="assets-filtered-empty">
                        No assets match your filters.
                      </div>
                    ) : (
                      <>
                        <div className="font-display text-lg text-slate-700">No assets yet</div>
                        <div className="text-sm text-slate-500 mt-1">
                          Add your first distressed asset, or import a batch to get started.
                        </div>
                        <div className="mt-4 flex items-center justify-center gap-2">
                          <Button
                            className="bg-slate-900 hover:bg-slate-800"
                            onClick={() => navigate(addAssetPath)}
                            data-testid="empty-state-add-asset"
                            style={{ display: cfg.noAdd ? "none" : undefined }}
                          >
                            <Plus className="h-4 w-4 mr-1.5" /> Add Asset
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => navigate("/bulk-import")}
                            data-testid="empty-state-import-assets"
                          >
                            <Upload className="h-4 w-4 mr-1.5" /> Import via Bulk Upload
                          </Button>
                        </div>
                      </>
                    )}
                  </td>
                </tr>
              ) : (
                assets.map((a) => {
                  const r = rels[a.id] || {};
                  return (
                    <tr
                      key={a.id}
                      className="border-b border-slate-100 cursor-pointer"
                      onClick={() => navigate(`/assets/${a.id}`)}
                      data-testid={`asset-row-${a.id}`}
                    >
                      <td>
                        <div className="font-mono-num text-[11px] text-slate-500">{a.asset_code}</div>
                        <Link to={`/assets/${a.id}`} className="text-sm font-semibold text-slate-900 hover:underline" onClick={(e) => e.stopPropagation()}>
                          {a.name}
                        </Link>
                      </td>
                      <td className="text-sm text-slate-700">
                        <div>{categoryMap[a.asset_category_id] || "—"}</div>
                        <div className="text-xs text-slate-500">{typeMap[a.asset_type_id] || "—"}</div>
                      </td>
                      <td className="text-sm text-slate-700">{cityMap[a.city_id] || "—"}</td>
                      <td className="text-right font-mono-num text-sm">{formatINRCompact(a.offer_price_inr)}</td>
                      <td className="text-sm text-slate-700">{saleTypeMap[a.sale_type_id] || "—"}</td>
                      <td><RelationshipCell type="offered" entries={r.offered_to} /></td>
                      <td><RelationshipCell type="brokers" entries={r.shared_with_brokers} /></td>
                      <td><RelationshipCell type="matched" entries={r.matched_leads} /></td>
                      <td><RelationshipCell type="matched_brokers" entries={r.matched_brokers} /></td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const TYPE_LABEL = {
  offered: "Offered To",
  matched: "Matched Leads",
  brokers: "Shared with Brokers",
  matched_brokers: "Matched Brokers",
};

function scoreTone(score) {
  if (score >= 75) return "text-emerald-700";
  if (score >= 50) return "text-amber-700";
  return "text-slate-600";
}

function RelationshipCell({ type, entries }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const list = entries || [];

  if (list.length === 0) {
    return <span className="text-slate-400" data-testid={`rel-empty-${type}`}>—</span>;
  }

  const visible = list.length > 4 ? list.slice(0, 3) : list;
  const overflow = list.length > 4 ? list.length - 3 : 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          className="flex flex-wrap items-center gap-1 max-w-[220px] cursor-pointer"
          onClick={(e) => e.stopPropagation()}
          data-testid={`rel-cell-${type}`}
        >
          {visible.map((e) => (
            <span
              key={e.id}
              className="inline-flex items-center gap-1 max-w-[120px] px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 border border-slate-200 text-slate-700"
            >
              {type === "brokers" && e.exclusive && (
                <Star className="h-2.5 w-2.5 text-[#B8935A] fill-[#B8935A] shrink-0" />
              )}
              <span className="truncate">{e.name}</span>
            </span>
          ))}
          {overflow > 0 && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-900 text-white"
              data-testid={`rel-overflow-${type}`}
            >
              +{overflow}
            </span>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 p-0"
        onClick={(e) => e.stopPropagation()}
        data-testid={`rel-popover-${type}`}
      >
        <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
          <div className="label-eyebrow !text-slate-600">{TYPE_LABEL[type]} ({list.length})</div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-slate-400 hover:text-slate-700"
            data-testid={`rel-popover-close-${type}`}
            aria-label="Close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
          {list.map((e) => (
            <div key={e.id} className="px-3 py-2 flex items-center gap-2">
              {type === "matched" && (
                <span className={`font-mono-num text-sm font-bold w-10 shrink-0 ${scoreTone(e.match_score || 0)}`}>
                  {e.match_score ?? 0}%
                </span>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1">
                  {type === "brokers" && e.exclusive && (
                    <Star className="h-3 w-3 text-[#B8935A] fill-[#B8935A] shrink-0" />
                  )}
                  {type === "matched_brokers" ? (
                    <button
                      type="button"
                      onClick={(ev) => { ev.stopPropagation(); navigate(`/brokers/${e.broker_id}`); }}
                      className="truncate text-left hover:underline text-slate-900"
                      data-testid={`matched-broker-link-${e.broker_id}`}
                    >
                      {e.name}
                    </button>
                  ) : (
                    e.name
                  )}
                </div>
                <div className="text-[11px] text-slate-500 truncate">
                  {type === "offered" && (e.offer_status || "—")}
                  {type === "matched" && (e.status || "—")}
                  {type === "matched_brokers" && (
                    <>
                      {e.broker_type || "—"}
                      {e.coverage_cities?.length > 0 && (
                        <span className="text-slate-400"> · {e.coverage_cities.join(", ")}</span>
                      )}
                    </>
                  )}
                  {type === "brokers" && (
                    <>
                      {e.share_status || "—"}
                      {e.exclusive && <span className="ml-1 text-[#B8935A] font-semibold">· Exclusive</span>}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

